import { usePageContextOptional } from "../contexts";
import { useTransferDirty } from "./use-data-selector";

/**
 * Result from useEditMode hook.
 */
export interface EditModeResult {
	/** Whether the current page is in edit mode (has unsaved changes) */
	isEditMode: boolean;
	/** The transfer ID being tracked */
	transferId: string | null;
}

/**
 * Hook to determine if the current page is in edit mode.
 *
 * Edit mode is derived from the DataStore's isDirty state for the current transfer.
 * When a user modifies any field on a VIEW page, the transfer becomes dirty and
 * the page enters edit mode. This affects:
 * - Visibility of Cancel/Save buttons (only shown in edit mode)
 * - Whether navigation away should prompt for unsaved changes
 * - Whether Create actions can navigate to new pages
 *
 * @returns EditModeResult with isEditMode and transferId
 */
export function useEditMode(): EditModeResult {
	const pageContext = usePageContextOptional();
	const transferId = pageContext?.transferId ?? null;

	// Use the existing useTransferDirty hook, but handle null transferId gracefully
	// When transferId is null or empty, we're not tracking any transfer
	const isDirty = useTransferDirty(transferId ?? "");

	return {
		isEditMode: transferId !== null && isDirty,
		transferId,
	};
}
